// frontend/src/pages/Login.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { FaUser, FaLock } from 'react-icons/fa';

// --- STYLES: Abhyaas Green design system. Visual-only re-skin.
//     Overrides the legacy navy/sage .login-* classes (from styles.css)
//     with green design tokens. No logic, markup, or class names changed. ---
const LoginStyles = () => (
    <style>{`
        /* ---- Page shell: split with forest-gradient panel + form card ---- */
        .login-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            padding: var(--s-5);
            background: var(--paper);
            font-family: var(--font-body);
        }

        /* Soft forest wash behind the card (replaces navy radial glow, no blur) */
        .login-page .login-background {
            position: absolute;
            inset: 0;
            background:
                radial-gradient(60% 55% at 12% 18%, rgba(34,163,90,.14), transparent 60%),
                radial-gradient(55% 50% at 88% 82%, rgba(20,83,45,.12), transparent 60%);
            opacity: 1;
        }

        /* ---- The form card ---- */
        .login-page .login-container {
            position: relative;
            width: 100%;
            max-width: 420px;
            background: var(--surface);
            border: 1px solid var(--line);
            border-radius: var(--r-xl);
            box-shadow: var(--e3);
            padding: var(--s-8) var(--s-7);
            margin: var(--s-4);
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
        }

        /* ---- Header ---- */
        .login-page .login-header {
            text-align: center;
            margin-bottom: var(--s-7);
        }

        /* Abhyaas wordmark at its natural aspect ratio — no square chip (which
           squished it) and no redundant "Abhyaas" heading below it. */
        .login-page .login-logo {
            width: 210px;
            max-width: 66%;
            height: auto;
            display: block;
            margin: 0 auto var(--s-5);
        }

        .login-page .login-header h1 {
            font-family: var(--font-display);
            font-size: var(--t-h1);
            line-height: var(--lh-h1);
            font-weight: 700;
            letter-spacing: -.02em;
            color: var(--green-800);
            margin: 0 0 var(--s-2) 0;
        }

        .login-page .login-header p {
            font-size: var(--t-label);
            font-weight: 700;
            letter-spacing: .12em;
            text-transform: uppercase;
            color: var(--green-600);
            margin: 0;
        }

        /* ---- Form ---- */
        .login-form {
            display: flex;
            flex-direction: column;
            gap: var(--s-4);
        }

        .login-form .form-group {
            position: relative;
        }

        .login-form .input-icon {
            position: absolute;
            top: 50%;
            left: 16px;
            transform: translateY(-50%);
            width: 18px;
            height: 18px;
            color: var(--ink-500);
            z-index: 1;
        }

        /* Inputs follow the design's .input spec */
        .login-form .form-input {
            width: 100%;
            height: 50px;
            padding: 0 16px 0 44px;
            font-family: var(--font-body);
            font-size: var(--t-body);
            color: var(--ink-900);
            background: var(--surface-sunken);
            border: 1.5px solid transparent;
            border-radius: var(--r-md);
            transition: border-color var(--dur-fast) var(--ease-std),
                        background var(--dur-fast) var(--ease-std),
                        box-shadow var(--dur-fast) var(--ease-std);
        }

        .login-form .form-input::placeholder {
            color: var(--ink-300);
        }

        .login-form .form-input:focus {
            outline: none;
            background: var(--surface);
            border-color: var(--green-600);
            box-shadow: 0 0 0 4px rgba(34,163,90,.12);
        }

        .login-form .form-input:disabled {
            opacity: .6;
        }

        /* ---- Primary "Sign In" pill ---- */
        .login-page .login-button {
            width: 100%;
            min-height: 50px;
            margin-top: var(--s-2);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: var(--s-2);
            font-family: var(--font-body);
            font-weight: 600;
            font-size: var(--t-body-lg);
            color: #fff;
            background: var(--green-600);
            border: none;
            border-radius: var(--r-pill);
            box-shadow: var(--glow-green);
            cursor: pointer;
            transition: transform var(--dur-fast) var(--ease-std),
                        background var(--dur-fast) var(--ease-std),
                        box-shadow var(--dur-fast) var(--ease-std);
        }

        .login-page .login-button:hover:not(:disabled) {
            background: var(--green-700);
            transform: translateY(-2px);
            box-shadow: var(--e3);
        }

        .login-page .login-button:active:not(:disabled) {
            transform: scale(.98);
        }

        .login-page .login-button:disabled {
            opacity: .55;
            cursor: not-allowed;
            box-shadow: none;
        }

        /* Spinner inside the button (re-tinted; not an idle animation) */
        .login-page .login-button .loading-spinner {
            width: 22px;
            height: 22px;
            border: 3px solid rgba(255,255,255,.45);
            border-top-color: #fff;
            border-radius: var(--r-pill);
        }

        /* ---- Footer ---- */
        .login-page .login-footer {
            text-align: center;
            margin-top: var(--s-6);
            font-size: var(--t-caption);
            line-height: var(--lh-caption);
            color: var(--ink-500);
        }

        .login-page { background: #06150f; padding: clamp(16px,3vw,44px); }
        .login-page .login-background {
            background:
                radial-gradient(42% 48% at 18% 18%, rgba(57,211,123,.23), transparent 68%),
                radial-gradient(40% 44% at 84% 80%, rgba(30,133,84,.2), transparent 72%),
                linear-gradient(145deg,#06150f 0%,#0a2418 52%,#071a12 100%);
            animation: login-emerald-drift 11s ease-in-out infinite alternate;
        }
        @keyframes login-emerald-drift { to { transform: scale(1.08) translate3d(2%,-1%,0); filter: saturate(1.18); } }
        .login-page .login-container {
            max-width: 920px; display: grid; grid-template-columns: minmax(0,.9fr) minmax(320px,1.1fr); gap: 12px;
            padding: 12px; margin: 0; border-radius: 32px; overflow: hidden;
            background: rgba(10,31,22,.74); border-color: rgba(143,217,164,.16);
            box-shadow: 0 34px 90px rgba(0,0,0,.42); backdrop-filter: blur(22px); -webkit-backdrop-filter: blur(22px);
        }
        .login-page .login-header { min-height: 470px; margin: 0; padding: 44px 38px; display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-end; text-align: left; border-radius: 24px; overflow: hidden; position: relative; background: linear-gradient(155deg,rgba(47,178,105,.18),rgba(7,39,28,.22)); border: 1px solid rgba(255,255,255,.07); }
        .login-page .login-header::before { content:''; position:absolute; width:300px; height:300px; border-radius:50%; left:-120px; top:-130px; background:rgba(83,235,147,.16); filter:blur(4px); }
        .login-brand-lockup { position: absolute; left: 32px; top: 30px; display: flex; align-items: center; gap: 12px; }
        .login-page .login-glyph { width: 66px; height: 66px; object-fit: contain; filter: drop-shadow(0 9px 20px rgba(0,0,0,.25)); }
        .login-page .login-wordmark { width: 146px; height: auto; filter: brightness(0) invert(1); opacity: .94; }
        .login-page .login-header h1 { color:#fff; font-size:clamp(34px,4vw,52px); line-height:1; letter-spacing:-.045em; margin-bottom:14px; }
        .login-page .login-header p { color:rgba(222,246,228,.72); letter-spacing:.08em; }
        .login-page .login-form { align-self: center; margin: 22px; padding: 34px; border-radius: 24px; background:rgba(5,20,14,.52); border:1px solid rgba(255,255,255,.07); }
        .login-form .form-input { height:56px; background:rgba(255,255,255,.055); border-color:rgba(255,255,255,.08); color:#f4faf6; }
        .login-form .form-input:focus { background:rgba(255,255,255,.075); border-color:#56c078; box-shadow:0 0 0 4px rgba(86,192,120,.11); }
        .login-page .login-button { min-height:56px; margin-top:8px; color:#0a2d1e; background:#f8fdf9; font-weight:750; box-shadow:0 14px 32px rgba(0,0,0,.2); }
        .login-page .login-button:hover:not(:disabled) { color:#062014; background:#dff7e6; }
        .login-page .login-footer { grid-column:2; margin:-9px 24px 18px; }
        @media (max-width:760px) {
            .login-page .login-container { max-width:470px; display:block; padding:9px; border-radius:27px; }
            .login-page .login-header { min-height:260px; padding:104px 25px 28px; border-radius:20px; }
            .login-page .login-header h1 { font-size:36px; }
            .login-brand-lockup { left:22px; top:20px; }
            .login-page .login-glyph { width:56px; height:56px; }
            .login-page .login-wordmark { width:128px; }
            .login-page .login-form { margin:9px 0 0; padding:25px 18px; border-radius:20px; }
            .login-page .login-footer { margin:17px 10px 12px; }
        }
    `}</style>
);


const Login = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            toast.error('Please enter both email and password');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/auth/login', { email, password });
            const { token, user } = response.data;
            toast.success(`Welcome back, ${user.name}!`);
            onLogin(user, token);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <LoginStyles />
            <div className="login-page">
                <div className="login-background"></div>
                <motion.div
                    className="login-container"
                    initial={{ opacity: 0, scale: 0.98, y: 12 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="login-header">
                        <div className="login-brand-lockup">
                            <img src="/glyph.png" alt="" className="login-glyph" />
                            <img src="/logo.svg" alt="Abhyaas" className="login-wordmark" />
                        </div>
                        <h1>Welcome back</h1>
                        <p>Sign in to continue</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <FaUser className="input-icon" />
                            <input
                                type="email"
                                placeholder="Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="form-input"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <FaLock className="input-icon" />
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="form-input"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            className="login-button"
                            disabled={loading}
                        >
                            {loading ? <span className="loading-spinner"></span> : 'Sign In'}
                        </button>
                    </form>

                    <div className="login-footer">
                        <p>Need help? Contact school administration.</p>
                    </div>
                </motion.div>
            </div>
        </>
    );
};

export default Login;
